import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  EyeOff,
  Eye,
  Lock,
  Users,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  Timer,
  Sparkles,
  ArrowUpDown,
  Trophy,
} from "lucide-react";

/**
 * /owner/lineups/:tie_id — Team-Owner double-blind lineup submission.
 *
 * Season 3 flow (mandated by user):
 *   1. Owner picks 6 players.
 *   2. Owner groups them into 3 pairs (Pair A / B / C — NOT seeds).
 *   3. System computes pair totals from CATEGORY_POINTS (A+=1 .. E=6).
 *   4. System auto-assigns seeds: LOWEST pair total = Seed 1, then 2, then 3.
 *   5. If two pairs are tied, owner picks which of the two is the higher seed.
 *   6. Read-only Seed 1/2/3 preview → Submit.
 *   7. Double-blind: opponent picks stay hidden until both submit; auto-lock on reveal.
 *
 * A+/international players may only appear in Seed 1 or Seed 2 — enforced
 * server-side against the SORTED result. If your pair with an A+ has the
 * highest total, submission is rejected; regroup.
 */

const CATEGORY_POINTS = { "A+": 1, A: 2, B: 3, C: 4, D: 5, E: 6 };
const pts = (p) => CATEGORY_POINTS[(p?.category || "").toUpperCase()] ?? 6;

/** Group 6 selected player IDs into 3 pairs of 2 (kept in selection order). */
function chunkPairs(playerIds) {
  const out = [[], [], []];
  playerIds.forEach((id, i) => {
    const pairIdx = Math.floor(i / 2);
    if (pairIdx < 3) out[pairIdx].push(id);
  });
  return out;
}

/** Auto-sort 3 pairs by total ASC. Returns sorted pairs, totals, and tied flag.
 *  When adjacent pairs share a total, `tied_flag` names them and we honour the
 *  user's incoming order for the tied pairs. */
function autoSortPairs(pairs, players) {
  const byId = Object.fromEntries((players || []).map((p) => [p.id, p]));
  const total = (ids) => ids.reduce((sum, id) => sum + pts(byId[id]), 0);
  const enriched = pairs.map((ids, idx) => ({ idx, ids, total: total(ids) }));
  enriched.sort((a, b) => a.total - b.total || a.idx - b.idx);
  let tied = null;
  if (enriched[0].total === enriched[1].total && enriched[1].total === enriched[2].total) tied = "all";
  else if (enriched[0].total === enriched[1].total) tied = "seed_1_vs_seed_2";
  else if (enriched[1].total === enriched[2].total) tied = "seed_2_vs_seed_3";
  return {
    sorted: enriched.map((e) => e.ids),
    totals: enriched.map((e) => e.total),
    tied,
    originalIndices: enriched.map((e) => e.idx),
  };
}

export default function OwnerLineup() {
  const { tie_id } = useParams();
  const [search] = useSearchParams();
  const previewTeamId = search.get("team_id");
  const [payload, setPayload] = useState(null);
  const [players, setPlayers] = useState([]);
  // pairs is a flat 6-slot array with pairs 0-1, 2-3, 4-5 (Pair A, B, C).
  const [pairs, setPairs] = useState([[], [], []]);
  const [busy, setBusy] = useState(false);
  const [appearance, setAppearance] = useState(null);

  const load = async () => {
    try {
      const url = previewTeamId
        ? `/lineups/mine/${tie_id}?team_id=${previewTeamId}`
        : `/lineups/mine/${tie_id}`;
      const { data } = await api.get(url);
      setPayload(data);
      if (data.my_lineup) {
        setPairs([
          (data.my_lineup.seed_1 || []).map((p) => p.id),
          (data.my_lineup.seed_2 || []).map((p) => p.id),
          (data.my_lineup.seed_3 || []).map((p) => p.id),
        ]);
      }
      const { data: teamPlayers } = await api.get(`/players?team_id=${data.team_id}`);
      setPlayers(teamPlayers || []);
      try {
        const { data: appMap } = await api.get(`/teams/${data.team_id}/appearance-map`);
        setAppearance(appMap);
      } catch {
        setAppearance(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load lineup");
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [tie_id, previewTeamId]);

  const selected = useMemo(() => new Set([...pairs[0], ...pairs[1], ...pairs[2]]), [pairs]);
  const totalSelected = pairs[0].length + pairs[1].length + pairs[2].length;

  /** Add the player to the first pair with an empty slot. */
  const addPlayer = (playerId) => {
    setPairs((prev) => {
      const next = prev.map((p) => [...p]);
      if (next.flat().includes(playerId)) return prev;
      for (let i = 0; i < 3; i++) {
        if (next[i].length < 2) {
          next[i].push(playerId);
          return next;
        }
      }
      toast.error("All 3 pairs are full — remove a player first.");
      return prev;
    });
  };

  const removePlayer = (pairIdx, playerId) => {
    setPairs((prev) => {
      const next = prev.map((p) => [...p]);
      next[pairIdx] = next[pairIdx].filter((id) => id !== playerId);
      return next;
    });
  };

  const clearAll = () => setPairs([[], [], []]);

  const autoSort = useMemo(() => autoSortPairs(pairs, players), [pairs, players]);

  /** Swap two pairs (used by the tiebreak resolver). */
  const swapSortedPositions = (a, b) => {
    if (a === b) return;
    // We rewrite the underlying `pairs` order so the new incoming order
    // becomes the tie-broken order the server will honour.
    setPairs((prev) => {
      const sortedIds = autoSort.sorted;
      const next = [...sortedIds];
      [next[a], next[b]] = [next[b], next[a]];
      return next;
    });
  };

  const seedPreview = useMemo(() => {
    if (totalSelected !== 6 || pairs.some((p) => p.length !== 2)) return null;
    return autoSort;
  }, [pairs, autoSort, totalSelected]);

  // Client-side validation mirrors backend (post-auto-sort).
  const validation = useMemo(() => {
    const errs = [];
    if (totalSelected !== 6) errs.push(`Need exactly 6 players (got ${totalSelected}).`);
    if (pairs.some((p) => p.length !== 2)) errs.push("Every pair must have exactly 2 players.");
    if (new Set([...pairs.flat()]).size !== pairs.flat().length) errs.push("Duplicate player detected.");
    // A+ Seed 3 check on the SORTED result (whichever pair ends up as Seed 3).
    if (seedPreview) {
      const seed3Ids = seedPreview.sorted[2];
      for (const pid of seed3Ids) {
        const p = players.find((x) => x.id === pid);
        if (p && ((p.category || "").toUpperCase() === "A+" || p.is_international)) {
          errs.push(
            `A+/international "${p.name}" ends up in Seed 3 — regroup so the A+ pair has a lower total.`
          );
        }
      }
    }
    return { ok: errs.length === 0, errors: errs };
  }, [pairs, players, seedPreview, totalSelected]);

  const strandingPreview = useMemo(() => {
    if (!appearance || !appearance.summary) return { stranded: [], onTrack: [], safe: true };
    const remainingBefore = appearance.summary.remaining_ties || 0;
    const alreadySubmittedThisTie = !!payload?.my_lineup;
    const remainingAfter = alreadySubmittedThisTie ? remainingBefore : Math.max(0, remainingBefore - 1);
    const currentIds = new Set(pairs.flat());
    const stranded = [];
    const onTrack = [];
    for (const p of appearance.players || []) {
      if (p.status === "met" || p.status === "exempt") continue;
      const alreadyCountedForThisTie =
        alreadySubmittedThisTie &&
        (payload?.my_lineup?.seed_1?.some((x) => x.id === p.player_id) ||
          payload?.my_lineup?.seed_2?.some((x) => x.id === p.player_id) ||
          payload?.my_lineup?.seed_3?.some((x) => x.id === p.player_id));
      const willAppear = currentIds.has(p.player_id);
      let projected = p.appearances;
      if (!alreadyCountedForThisTie && willAppear) projected += 1;
      else if (alreadyCountedForThisTie && !willAppear) projected -= 1;
      const stillNeeds = Math.max(0, 2 - projected);
      if (stillNeeds > remainingAfter) stranded.push({ ...p, projected, stillNeeds });
      else if (stillNeeds > 0) onTrack.push({ ...p, projected, stillNeeds });
    }
    return { stranded, onTrack, safe: stranded.length === 0, remainingAfter };
  }, [appearance, pairs, payload]);

  const submit = async () => {
    if (!validation.ok) {
      toast.error(validation.errors[0]);
      return;
    }
    if (!seedPreview) {
      toast.error("Complete all 3 pairs first.");
      return;
    }
    setBusy(true);
    try {
      // Send in the tie-broken order — server re-sorts by total and honours
      // incoming order for ties.
      const [s1, s2, s3] = seedPreview.sorted;
      const { data } = await api.post(`/lineups/${tie_id}`, {
        seed_1: s1,
        seed_2: s2,
        seed_3: s3,
      });
      toast.success(
        data.revealed
          ? "Both lineups submitted — REVEALED and locked."
          : "Lineup submitted — hidden from opponent until they submit."
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally {
      setBusy(false);
    }
  };

  if (!payload) return <div className="p-8 text-zinc-400">Loading lineup…</div>;

  const kick = payload.scheduled_at ? new Date(payload.scheduled_at) : null;
  const minsToKick = kick ? Math.floor((kick.getTime() - Date.now()) / 60000) : null;
  const past15Cutoff = minsToKick !== null && minsToKick <= 15;

  return (
    <div className="p-6 md:p-8 min-h-screen bg-[#060d09] text-white" data-testid="owner-lineup-page">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Users className="w-5 h-5 text-[#F4B400]" />
          <h1 className="font-anton text-4xl uppercase tracking-tight">Team Lineup Submission</h1>
        </div>
        <p className="text-sm text-zinc-400 max-w-3xl">
          Pick 6 players, group them into 3 pairs. The system auto-assigns Seed 1/2/3 based on
          category-points totals (A+=1 · A=2 · B=3 · C=4 · D=5 · E=6). <b className="text-[#F4B400]">Lowest total = Seed 1.</b>
        </p>
      </div>

      <StatusBanner payload={payload} pastCutoff={past15Cutoff} minsToKick={minsToKick} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        {/* Left: available players */}
        <Card className="p-4 bg-[#0f1a15] border-white/10" data-testid="lineup-available-pool">
          <div className="flex items-center justify-between mb-3">
            <div className="font-anton text-lg uppercase tracking-tight text-[#00A86B]">
              Available Players
            </div>
            {totalSelected > 0 && !payload.locked && (
              <button
                onClick={clearAll}
                className="text-[10px] uppercase text-zinc-500 hover:text-red-300"
                data-testid="lineup-clear-btn"
              >
                Clear
              </button>
            )}
          </div>
          <div className="space-y-1.5">
            {players.map((p) => {
              const isSel = selected.has(p.id);
              const cp = pts(p);
              const isAplus = (p.category || "").toUpperCase() === "A+" || p.is_international;
              return (
                <button
                  key={p.id}
                  onClick={() => !isSel && !payload.locked && addPlayer(p.id)}
                  disabled={isSel || payload.locked}
                  data-testid={`lineup-player-${p.id}`}
                  className={`w-full text-left p-2 rounded-lg border flex items-center justify-between transition ${
                    isSel
                      ? "border-white/5 bg-black/10 opacity-40 cursor-not-allowed"
                      : "border-white/10 bg-black/30 hover:border-[#F4B400] hover:bg-[#F4B400]/5"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`w-6 h-6 rounded-md text-[10px] font-bold flex items-center justify-center ${
                        isSel ? "bg-white/10 text-zinc-500" : "bg-[#F4B400]/20 text-[#F4B400]"
                      }`}
                    >
                      {cp}
                    </span>
                    <div>
                      <div className="text-sm text-white leading-tight">{p.name}</div>
                      <div className="text-[10px] text-zinc-500">
                        Cat {p.category || "?"}
                        {isAplus && " · INTL"}
                      </div>
                    </div>
                  </div>
                  <span className="text-[10px] uppercase font-bold text-zinc-500">
                    {isSel ? "Added" : "+ Add"}
                  </span>
                </button>
              );
            })}
            {!players.length && (
              <div className="text-xs text-zinc-500 italic py-4 text-center">
                No players in your roster.
              </div>
            )}
          </div>
        </Card>

        {/* Middle: my pairs */}
        <Card className="p-4 bg-[#0f1a15] border-white/10 lg:col-span-1" data-testid="lineup-my-picks">
          <div className="font-anton text-lg uppercase tracking-tight text-[#F4B400] mb-1">
            Your 3 Pairs
          </div>
          <div className="text-[10px] text-zinc-500 mb-3">
            Group any 6 players into 3 pairs. Seed is assigned by pair total, not by you.
          </div>
          {pairs.map((pair, pairIdx) => {
            const pairTotal = pair.reduce(
              (sum, id) => sum + pts(players.find((x) => x.id === id)),
              0
            );
            return (
              <div key={pairIdx} className="mb-3" data-testid={`lineup-pair-${pairIdx}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">
                    Pair {String.fromCharCode(65 + pairIdx)} · {pair.length}/2
                  </div>
                  {pair.length === 2 && (
                    <div className="text-[10px] uppercase text-[#F4B400] font-bold">
                      Total: <span className="text-sm tabular-nums">{pairTotal}</span>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {[0, 1].map((idx) => {
                    const pid = pair[idx];
                    const p = pid ? players.find((x) => x.id === pid) : null;
                    return (
                      <div
                        key={idx}
                        className={`p-2 rounded-lg border min-h-[52px] flex items-center justify-between ${
                          p
                            ? "border-[#F4B400]/30 bg-[#F4B400]/5"
                            : "border-dashed border-white/10 bg-black/20"
                        }`}
                        data-testid={`lineup-pair-${pairIdx}-slot-${idx}`}
                      >
                        {p ? (
                          <>
                            <div className="text-xs text-white truncate">
                              {p.name}
                              <span className="text-zinc-500 text-[10px] ml-1">
                                Cat {p.category} · {pts(p)}pt
                              </span>
                            </div>
                            {!payload.locked && (
                              <button
                                onClick={() => removePlayer(pairIdx, p.id)}
                                className="text-[9px] text-red-400 hover:text-red-300 uppercase tracking-widest"
                                data-testid={`lineup-pair-${pairIdx}-remove-${idx}`}
                              >
                                ×
                              </button>
                            )}
                          </>
                        ) : (
                          <div className="text-[10px] text-zinc-600 italic text-center pt-1 w-full">
                            empty
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Auto-seed preview */}
          {seedPreview && (
            <SeedPreviewCard
              seedPreview={seedPreview}
              pairs={pairs}
              players={players}
              onSwap={swapSortedPositions}
              locked={payload.locked}
            />
          )}

          <div className="mt-4 space-y-1" data-testid="lineup-validation">
            {validation.errors.length ? (
              validation.errors.map((e, i) => (
                <div key={i} className="flex items-start gap-1.5 text-[11px] text-red-300">
                  <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                  {e}
                </div>
              ))
            ) : (
              <div className="flex items-center gap-1.5 text-[11px] text-emerald-300">
                <CheckCircle2 className="w-3 h-3" />
                Lineup passes all checks.
              </div>
            )}
          </div>

          {appearance &&
            (strandingPreview.stranded.length > 0 || strandingPreview.onTrack.length > 0) && (
              <div
                className={`mt-3 p-3 rounded-lg border ${
                  strandingPreview.stranded.length
                    ? "bg-red-500/10 border-red-500/40"
                    : "bg-amber-500/10 border-amber-500/30"
                }`}
                data-testid="lineup-appearance-warning"
              >
                <div
                  className={`flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold mb-1 ${
                    strandingPreview.stranded.length ? "text-red-300" : "text-amber-300"
                  }`}
                >
                  <AlertTriangle className="w-3 h-3" />
                  {strandingPreview.stranded.length
                    ? `${strandingPreview.stranded.length} player(s) will be stranded — submit will fail`
                    : `${strandingPreview.onTrack.length} player(s) still need appearances`}
                </div>
                <div className="text-[11px] text-zinc-300 leading-snug">
                  Min-appearance rule: every squad player must play in ≥2 league ties. After this
                  submission {strandingPreview.remainingAfter} tie(s) will remain.
                </div>
                {strandingPreview.stranded.length > 0 && (
                  <ul className="mt-2 space-y-0.5" data-testid="lineup-stranded-list">
                    {strandingPreview.stranded.map((p) => (
                      <li key={p.player_id} className="text-[11px] text-red-200">
                        • <b>{p.player_name}</b> — has {p.projected}, needs {p.stillNeeds} more with
                        only {strandingPreview.remainingAfter} tie(s) left
                      </li>
                    ))}
                  </ul>
                )}
                {strandingPreview.onTrack.length > 0 && strandingPreview.stranded.length === 0 && (
                  <ul className="mt-2 space-y-0.5" data-testid="lineup-ontrack-list">
                    {strandingPreview.onTrack.map((p) => (
                      <li key={p.player_id} className="text-[11px] text-amber-200">
                        • <b>{p.player_name}</b> — has {p.projected}, still needs {p.stillNeeds} in
                        remaining tie(s)
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

          <Button
            onClick={submit}
            disabled={!validation.ok || busy || payload.locked}
            className="mt-4 w-full bg-[#F4B400] text-black font-bold uppercase tracking-widest hover:bg-[#e5aa00] disabled:opacity-40"
            data-testid="lineup-submit-btn"
          >
            {busy ? "Submitting…" : payload.my_status === "SUBMITTED" ? "Update Lineup" : "Submit Lineup"}
          </Button>
        </Card>

        {/* Right: opponent state */}
        <Card className="p-4 bg-[#0f1a15] border-white/10" data-testid="lineup-opponent-panel">
          <div className="font-anton text-lg uppercase tracking-tight mb-3 flex items-center gap-1.5">
            {payload.revealed ? (
              <Eye className="w-4 h-4 text-[#00A86B]" />
            ) : (
              <EyeOff className="w-4 h-4 text-zinc-500" />
            )}
            Opponent
          </div>
          {payload.revealed && payload.opponent_lineup ? (
            <div>
              {["seed_1", "seed_2", "seed_3"].map((slot) => (
                <div key={slot} className="mb-3">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">
                    {slot.replace("_", " ")}
                    {payload.opponent_lineup.pair_totals?.[slot] !== undefined && (
                      <span className="ml-2 text-[9px] text-[#F4B400]">
                        · Total {payload.opponent_lineup.pair_totals[slot]}
                      </span>
                    )}
                  </div>
                  {payload.opponent_lineup[slot].map((p, i) => (
                    <div
                      key={i}
                      className="p-2 rounded-lg border border-white/10 bg-black/30 mb-1 text-xs text-white"
                    >
                      {p.name}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <EyeOff className="w-10 h-10 mx-auto text-zinc-700 mb-2" />
              <div className="text-xs text-zinc-500 italic mb-1">Hidden until both teams submit</div>
              <div
                className={`text-[11px] font-bold uppercase tracking-widest ${
                  payload.opponent_status === "SUBMITTED" ? "text-emerald-400" : "text-zinc-400"
                }`}
              >
                Opponent: {payload.opponent_status}
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function SeedPreviewCard({ seedPreview, pairs, players, onSwap, locked }) {
  const byId = Object.fromEntries((players || []).map((p) => [p.id, p]));
  const seedLabels = ["Seed 1", "Seed 2", "Seed 3"];
  const seedColors = ["#F4B400", "#00A86B", "#7DD3FC"];

  return (
    <div
      className="mt-2 mb-3 p-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5"
      data-testid="lineup-auto-seed-preview"
    >
      <div className="flex items-center gap-1.5 mb-2">
        <Sparkles className="w-4 h-4 text-emerald-400" />
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold">
          Auto-Seeded Result (read-only)
        </div>
      </div>
      {seedPreview.tied && !locked && (
        <div
          className="mb-2 p-2 rounded bg-amber-500/15 border border-amber-500/40 text-[11px] text-amber-100"
          data-testid="lineup-tied-alert"
        >
          <div className="flex items-start gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div>
              <b>Tied pair totals detected.</b> You must break the tie. Use the swap arrows below to
              choose which pair is the higher seed. (Otherwise the current order is used.)
            </div>
          </div>
        </div>
      )}
      {seedPreview.sorted.map((ids, i) => {
        const canSwapUp = i > 0 && seedPreview.totals[i] === seedPreview.totals[i - 1];
        return (
          <div
            key={i}
            className="flex items-center gap-2 mb-1.5 p-2 rounded bg-black/30 border border-white/5"
            data-testid={`lineup-seed-preview-${i + 1}`}
          >
            <div
              className="w-14 text-[10px] uppercase tracking-widest font-bold"
              style={{ color: seedColors[i] }}
            >
              {seedLabels[i]}
            </div>
            <div className="flex-1 text-xs text-white">
              {ids.map((id) => byId[id]?.name || "?").join(" + ")}
            </div>
            <div className="text-[10px] uppercase text-[#F4B400] font-bold">
              Total <span className="text-sm tabular-nums">{seedPreview.totals[i]}</span>
            </div>
            {canSwapUp && !locked && (
              <button
                onClick={() => onSwap(i, i - 1)}
                title="Swap with pair above"
                className="text-zinc-400 hover:text-[#F4B400]"
                data-testid={`lineup-swap-${i}`}
              >
                <ArrowUpDown className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        );
      })}
      <div className="text-[10px] text-zinc-500 italic mt-2 flex items-start gap-1.5">
        <Trophy className="w-3 h-3 mt-0.5 shrink-0" />
        Seed 1 is the pair with the LOWEST category-points total. You cannot manually change the
        seed labels.
      </div>
    </div>
  );
}

function StatusBanner({ payload, pastCutoff, minsToKick }) {
  if (payload.revealed) {
    return (
      <Card
        className="p-3 bg-emerald-500/10 border-emerald-500/40 flex items-start gap-3"
        data-testid="lineup-status-revealed"
      >
        <Lock className="w-4 h-4 text-emerald-400 mt-0.5" />
        <div>
          <div className="font-bold uppercase tracking-widest text-emerald-300 text-xs">
            Lineups Revealed & Locked
          </div>
          <div className="text-xs text-zinc-300 mt-0.5">
            Both teams have submitted. Ask Super Admin to unlock if an emergency change is needed.
          </div>
        </div>
      </Card>
    );
  }
  if (payload.my_status === "SUBMITTED") {
    return (
      <Card
        className="p-3 bg-[#F4B400]/10 border-[#F4B400]/40 flex items-start gap-3"
        data-testid="lineup-status-waiting-opponent"
      >
        <EyeOff className="w-4 h-4 text-[#F4B400] mt-0.5" />
        <div>
          <div className="font-bold uppercase tracking-widest text-[#F4B400] text-xs">
            Waiting for opponent
          </div>
          <div className="text-xs text-zinc-300 mt-0.5">
            Your lineup is submitted and hidden from the opponent. As soon as they submit, both
            lineups reveal automatically.
          </div>
        </div>
      </Card>
    );
  }
  if (pastCutoff) {
    return (
      <Card
        className="p-3 bg-red-500/10 border-red-500/40 flex items-start gap-3"
        data-testid="lineup-status-past-cutoff"
      >
        <Timer className="w-4 h-4 text-red-400 mt-0.5" />
        <div>
          <div className="font-bold uppercase tracking-widest text-red-300 text-xs">
            Auto-fill window active
          </div>
          <div className="text-xs text-zinc-300 mt-0.5">
            Less than 15 min to kickoff. If you don't submit now, the system will auto-fill your
            lineup based on category points.
          </div>
        </div>
      </Card>
    );
  }
  return (
    <Card
      className="p-3 bg-white/5 border-white/10 flex items-start gap-3"
      data-testid="lineup-status-open"
    >
      <ShieldCheck className="w-4 h-4 text-zinc-400 mt-0.5" />
      <div>
        <div className="font-bold uppercase tracking-widest text-zinc-300 text-xs">
          Lineup submission open
        </div>
        <div className="text-xs text-zinc-500 mt-0.5">
          {minsToKick !== null ? `~${Math.max(0, minsToKick)} min to kickoff · ` : ""}
          Cutoff is 15 min before the first seed match starts.
        </div>
      </div>
    </Card>
  );
}
